import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, ListGroup, Alert } from 'react-bootstrap';
import api from '../../services/api';

interface TeamMember {
  id: number;
  name: string;
}

interface Team {
  id: number;
  name: string;
  username: string;
  team_members?: TeamMember[];
  teamMembers?: TeamMember[];
}

interface TeamMembersModalProps {
  show: boolean;
  onHide: () => void;
  team: Team | null;
  onUpdate: () => void;
}

const TeamMembersModal: React.FC<TeamMembersModalProps> = ({ show, onHide, team, onUpdate }) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [newMemberName, setNewMemberName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadMembers = async () => {
      if (team && show) {
        try {
          const response = await api.get(`/admin/teams/${team.id}`);
          const updatedTeam = response.data;
          setMembers(updatedTeam.team_members || updatedTeam.teamMembers || []);
        } catch (error) {
          // Fallback ai dati del team passati come prop
          setMembers(team.team_members || team.teamMembers || []);
        }
      }
    };

    loadMembers();
  }, [team, show]);

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newMemberName.trim()) {
      setError('Inserisci un nome per il membro');
      return;
    }

    if (!team) return;

    setLoading(true);

    try {
      const response = await api.post(`/admin/teams/${team.id}/members`, {
        name: newMemberName.trim(),
      });

      setMembers([...members, response.data.member]);
      setNewMemberName('');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore nell\'aggiunta del membro');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId: number) => {
    if (!window.confirm('Eliminare questo membro?')) {
      return;
    }

    if (!team) return;

    try {
      await api.delete(`/admin/teams/${team.id}/members/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      onUpdate();
    } catch (err: any) {
      alert('Errore nell\'eliminazione del membro');
    }
  };

  if (!team) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Membri del Team: {team.username}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Form per aggiungere membro */}
        <Form onSubmit={handleAddMember} className="mb-4">
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Nome del nuovo membro (es: Priscilla)"
              value={newMemberName}
              onChange={(e) => setNewMemberName(e.target.value)}
              disabled={loading}
            />
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Aggiunta...' : '+ Aggiungi'}
            </Button>
          </div>
        </Form>

        {/* Lista membri */}
        {members.length > 0 ? (
          <ListGroup>
            {members.map((member) => (
              <ListGroup.Item key={member.id} className="d-flex justify-content-between align-items-center">
                <span>{member.name}</span>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDeleteMember(member.id)}
                >
                  Elimina
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <p className="text-muted text-center py-3">Nessun membro nel team</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Chiudi
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TeamMembersModal;
